// Lightweight GA4 event helper + scroll-depth + outbound-click auto-tracking.
// Any tracked event marks a GA4 session as "engaged", so instrumenting real
// interactions is what actually moves the reported bounce rate.

type GtagFn = (...args: unknown[]) => void;

const getGtag = (): GtagFn | null => {
  if (typeof window === "undefined") return null;
  const g = (window as unknown as { gtag?: GtagFn }).gtag;
  return typeof g === "function" ? g : null;
};

export const trackEvent = (name: string, params: Record<string, unknown> = {}) => {
  const gtag = getGtag();
  if (!gtag) return;
  gtag("event", name, params);
};

/** Fire once per session at 25/50/75/100% scroll. */
export const initScrollDepthTracking = () => {
  if (typeof window === "undefined") return;
  const fired = new Set<number>();
  const thresholds = [25, 50, 75, 100];
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const height = Math.max(doc.scrollHeight, 1);
      const pct = Math.min(100, Math.round((scrolled / height) * 100));
      for (const t of thresholds) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          trackEvent("scroll_depth", { percent: t });
        }
      }
      if (fired.size === thresholds.length) {
        window.removeEventListener("scroll", onScroll);
      }
      ticking = false;
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
};

/** Track outbound clicks (RoomRaccoon, socials, etc.). */
export const initOutboundClickTracking = () => {
  if (typeof window === "undefined") return;
  document.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement | null)?.closest?.("a");
    if (!target) return;
    const href = target.getAttribute("href");
    if (!href || !/^https?:\/\//i.test(href)) return;
    try {
      const url = new URL(href);
      if (url.hostname === window.location.hostname) return;
      trackEvent("outbound_click", {
        url: href,
        domain: url.hostname,
        text: (target.textContent || "").trim().slice(0, 80),
      });
    } catch {
      /* ignore */
    }
  });
};

export const trackCtaClick = (label: string, location: string) => {
  trackEvent("cta_click", { label, location });
};
